import { type InputHTMLAttributes, type ReactNode, forwardRef, useId } from 'react';
import styles from './Input.module.css';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label: string;
  /** Visually hide label (keep for screen readers via sr-only). */
  hideLabel?: boolean;
  hint?: string;
  error?: string | undefined;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hideLabel = false, hint, error, leadingIcon, trailingIcon, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  // FE-55: visible required marker; F54 (WHS-F13) — `*` в `--text-muted`, не в danger.
  // Post-design-review: detect if label already ends with `*` (legacy i18n keys
  // like "SKU *") to avoid duplicate `* *`.
  const labelEndsWithAsterisk = typeof label === 'string' && label.trim().endsWith('*');
  const isRequired = Boolean(rest.required) && !labelEndsWithAsterisk;

  return (
    <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
      <label
        htmlFor={inputId}
        className={styles.label}
        style={
          hideLabel
            ? {
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
              }
            : undefined
        }
      >
        {label}
        {isRequired && !hideLabel && (
          <span className={styles.requiredMark} aria-hidden="true">
            *
          </span>
        )}
      </label>
      <div
        className={[styles.inputWrap, error ? styles.inputWrapError : ''].filter(Boolean).join(' ')}
      >
        {leadingIcon && <span className={styles.leading}>{leadingIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          className={styles.input}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        {trailingIcon && <span className={styles.trailing}>{trailingIcon}</span>}
      </div>
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
